import { TrendingDown, Eye, Lock, AlertTriangle, ArrowLeftRight, Ticket } from "lucide-react";
import { formatTime } from "@/components/trips/itinerary/itinerary-utils";
import type { PlanLeg, RebookAdvice, RebookAlternative } from "@/lib/types";
import CancellationCountdown from "./CancellationCountdown";

/**
 * What the traveller must see under a BOOKED flight, not behind a pulldown:
 * the record on file, the free-cancellation clock, and the rebook nudge. Renders
 * nothing for a leg that is merely watched.
 */
export default function LegBookingNotes({
  leg,
  passengers,
  onRebook,
}: {
  leg: PlanLeg;
  passengers?: number | null;
  onRebook: () => void;
}) {
  if (!leg.bookingStatus || leg.bookingStatus === "none") return null;
  return (
    <div>
      <BookedFlightNote leg={leg} passengers={passengers} />
      {leg.bookingStatus === "booked" && leg.cancellationWindow && (
        <CancellationCountdown cancellation={leg.cancellationWindow} />
      )}
      {leg.bookingStatus === "booked" && leg.rebook && (
        <RebookNudge rebook={leg.rebook} onRebook={onRebook} />
      )}
    </div>
  );
}

/** What we hold on record for a booked leg (B4). Says the departure out loud,
 *  because the whole class of bug this closes is the page describing a
 *  DIFFERENT flight than the one the traveller bought.
 *
 *  It also RECONCILES the two money figures on screen. The trip total counts a
 *  booked leg at what was PAID (R1 × B4), while the flight card above shows
 *  today's fare — so on 2026-08-24 Wade read $489 out + $918 back against a
 *  $2,684 total and correctly said the arithmetic did not work. Both numbers
 *  were right; nothing on the page said they were different facts. Now it does.
 */
function BookedFlightNote({ leg, passengers }: { leg: PlanLeg; passengers?: number | null }) {
  const money = (n: number) => "$" + Math.round(n).toLocaleString();
  const bits: string[] = [];
  if (leg.bookingFlightLabel) bits.push(leg.bookingFlightLabel);
  if (leg.bookingPaidPerPerson != null) bits.push(money(leg.bookingPaidPerPerson) + " per person");
  if (leg.bookingPaidTotal != null && leg.bookingPaidTotal !== leg.bookingPaidPerPerson) {
    bits.push(money(leg.bookingPaidTotal) + " total");
  }
  // A hold moves no money: what the airline quoted is shown as quoted, never as paid.
  if (leg.bookingStatus === "held" && leg.bookingQuotedTotal != null) {
    bits.push(money(leg.bookingQuotedTotal) + " quoted by the airline, not paid");
  }
  if (leg.bookingSeats && leg.bookingSeats.length > 0) {
    bits.push((leg.bookingSeats.length === 1 ? "seat " : "seats ") + leg.bookingSeats.join(", "));
  }
  if (leg.bookingRef) bits.push("confirmation " + leg.bookingRef.toUpperCase());
  const untracked = leg.bookingFlightTracked === false;

  // The reconciliation line. Only drawn when we actually hold BOTH facts for the
  // SAME departure — a live fare for another flight would restate the very
  // confusion this is here to remove.
  const paidPP = leg.bookingPaidPerPerson ?? null;
  const livePP = leg.bookingFlightTracked && leg.focus?.price != null ? leg.focus.price : null;
  const pax = passengers && passengers > 0 ? passengers : null;
  const legTotal = leg.bookingPaidTotal ?? (paidPP != null && pax ? paidPP * pax : null);
  const gap = paidPP != null && livePP != null ? Math.round(livePP - paidPP) : null;

  if (bits.length === 0 && !untracked) return null;

  return (
    <div className="mt-2 space-y-1">
      {bits.length > 0 && (
        <div className="text-xs text-muted-foreground">
          {/* T1 — a paid price is sourced from the traveller, not scraped. Say so. */}
          <span className="font-medium text-foreground">On record:</span> {bits.join(" · ")}
          <span className="ml-1 text-[11px]">(you told us)</span>
        </div>
      )}
      {legTotal != null && (
        <div className="text-xs text-muted-foreground">
          Counts as <span className="font-mono text-foreground">{money(legTotal)}</span> in your trip
          total.
          {livePP != null && gap != null && (
            <>
              {" "}
              The <span className="font-mono">{money(livePP)}</span> above is today&apos;s fare for
              the same flight —{" "}
              {gap === 0 ? (
                "the same as you paid"
              ) : (
                <span className={gap > 0 ? "text-amber-700" : "text-emerald-700"}>
                  {money(Math.abs(gap))} {gap > 0 ? "more" : "less"} than you paid
                </span>
              )}
              .
            </>
          )}
        </div>
      )}
      {untracked && (
        <div className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            We aren&apos;t tracking that exact departure yet, so the fares below describe other
            flights on this route — not the one you hold.
          </span>
        </div>
      )}
    </div>
  );
}

/** "6:12 AM Delta DL1234, 1 stop" — enough to know WHICH flight is meant. */
function describeAlternative(alt: RebookAlternative): string {
  const bits: string[] = [];
  const t = formatTime(alt.departLocal);
  if (t) bits.push(t);
  if (alt.airline) bits.push(alt.airline);
  if (alt.flightNumber) bits.push(alt.flightNumber);
  const who = bits.join(" ");
  const stops =
    alt.stops == null ? null : alt.stops === 0 ? "nonstop" : alt.stops + " stop" + (alt.stops === 1 ? "" : "s");
  return [who || "another flight", stops].filter(Boolean).join(", ");
}

/**
 * Post-booking fare watch. Only ever proposes — never cancels.
 *
 * ⚠️ TWO DIFFERENT FACTS, never merged. "Your fare dropped" is only true of the
 * flight you hold; a cheaper seat on a different departure is a SWITCH, and
 * saying so is the difference between a real number and an invented one. On
 * 2026-08-24 this box read "Fare dropped $597 below what you paid" beside a
 * "Paid $918" badge and a "$918" fare — the $597 was a Delta red-eye.
 */
function RebookNudge({ rebook, onRebook }: { rebook: RebookAdvice; onRebook: () => void }) {
  const alt = rebook.alternative ?? null;

  // 1. The fare on YOUR flight actually fell far enough to act on.
  if (rebook.worthRebooking) {
    return (
      <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
        <div className="flex items-start gap-2">
          <TrendingDown className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" aria-hidden="true" />
          <div className="min-w-0 flex-1 text-sm">
            <div className="font-medium text-emerald-900">
              Your own flight is ${rebook.savings} cheaper than you paid
            </div>
            <div className="mt-0.5 text-xs text-emerald-800">
              Same flight, same day: you paid ${rebook.pricePaid}, it is ${rebook.currentFare} now.
              Cancelling and rebooking could save ${rebook.savings} per person. {rebook.creditNote}
            </div>
            <button
              type="button"
              onClick={onRebook}
              className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-emerald-700 px-2.5 py-1 text-xs font-medium text-white transition-opacity hover:opacity-90"
            >
              <Ticket className="h-3.5 w-3.5" aria-hidden="true" /> See the cheaper fare
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (rebook.lockedReason) {
    return (
      <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Lock className="h-3.5 w-3.5" aria-hidden="true" /> {rebook.lockedReason}
      </div>
    );
  }

  // 2. Your fare held, but a DIFFERENT flight that day is cheaper. Say which
  //    one, and say it is a different flight — never call it a fare drop.
  if (alt && alt.worthSwitching) {
    return (
      <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5">
        <div className="flex items-start gap-2">
          <ArrowLeftRight className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" aria-hidden="true" />
          <div className="min-w-0 flex-1 text-sm">
            <div className="font-medium text-sky-900">
              A different flight that day is ${alt.savings} cheaper
            </div>
            <div className="mt-0.5 text-xs text-sky-800">
              Your fare has not moved. {describeAlternative(alt)} is ${alt.price} per person against
              the ${rebook.pricePaid} you paid — a different departure, same day. {rebook.creditNote}
            </div>
            <button
              type="button"
              onClick={onRebook}
              className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-sky-700 px-2.5 py-1 text-xs font-medium text-white transition-opacity hover:opacity-90"
            >
              <Ticket className="h-3.5 w-3.5" aria-hidden="true" /> See that flight
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 3. Nothing to act on. Say what we are actually watching, so silence is
  //    legible rather than mistaken for "we stopped looking".
  const held =
    rebook.currentFare != null && rebook.pricePaid != null && rebook.savings <= 0
      ? " · your fare is holding at $" + rebook.currentFare
      : rebook.savings > 0
        ? ` · $${rebook.savings} cheaper so far (below the $${rebook.threshold} rebook bar)`
        : "";
  return (
    <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Eye className="h-3.5 w-3.5" aria-hidden="true" />
      Watching the flight you booked
      {held}
    </div>
  );
}
